import { observer } from "mobx-react";
import { useAppSettingStore } from "./SettingStores/AppSettingPageStore";
import { useEffect, useState } from "react";
import { Route, Switch, useParams } from "react-router-dom";
import InlineSpinner from "../Spinner/InlineSpinner";
import { PageBreadcrumbs } from "../Breadcrumbs/PageBreadcrumbs";
import { Button, Colors, Display, Sizes } from "../Button/Button";
import { useValidation } from "Hooks/useValidation";
import ButtonAsyncState from "../Button/ButtonAsyncState";
import { CreditProviderForm } from "./Gibs/CreditProviderForm";
import { ProviderTypeSelector } from "./Gibs/ProviderTypeSelector";
import { OrganisationProviderList } from "./Gibs/OrganisationProviderList";
import HandleEvents from "Util/HandleEvents";
import classNames from "classnames";
import { action } from "mobx";
import { store } from "Models/Store";
import { CreditProviderProductList } from "./CreditProviderProductList";
import { CreditProviderProductDetail } from "./CreditProviderProductDetail";
import { ProviderProductUploadModal } from "./uploadProviderProduct/ProviderProductUploadModal";

interface Props {
	isNew?: boolean;
}

type availableSection = "details" | "organisations" | "products";

export const CreditProviderItemDetail: React.FunctionComponent<Props> = observer(({
	isNew = false,
}) => {
	const { providerId } = useParams<{ providerId: string, productId: string }>();
	const { creditProviderItemStore } = useAppSettingStore();

	const [sectionsToShow, setSectionsToShow] = useState<availableSection[]>(["details"]);
	const [showUploadProductModal, setShowUploadProductModal] = useState(false);

	const handleSectionToggle = (section: availableSection) => {
		if (sectionsToShow.includes(section)) {
			setSectionsToShow(sectionsToShow.filter(s => s !== section));
		} else {
			setSectionsToShow([...sectionsToShow, section]);
		}
	}

	const renderSection = (section: availableSection, title: string, content: React.ReactNode) => (
		<div className="credit-provider__section">
			<div
				className={classNames("section-label", { active: sectionsToShow.includes(section) })}
				{...HandleEvents(() => handleSectionToggle(section))}
			>
				<h6>{title}</h6>
			</div>
			{sectionsToShow.includes(section) && (
				<div className="section-panel">
					{content}
				</div>
			)}
		</div>
	)

	const validation = useValidation({
		name: {
			value: creditProviderItemStore.creditProvider.name,
			isRequired: true,
			requiredText: "Name is required.",
		},
		description: {
			value: creditProviderItemStore.creditProvider.description,
			isRequired: true,
			requiredText: "Description is required.",
			validate: value => value.assertLengthBetween(5, 1000, "Description must be between 5 and 1000 characters."),
		},
		providerTypeId: {
			value: creditProviderItemStore.creditProvider.providerTypeId,
			isRequired: true,
			requiredText: "Provider type is required.",
		},
	});

	useEffect(() => {
		if (isNew) {
			creditProviderItemStore.updateCreditProvider({ ...creditProviderItemStore.creditProvider, isActive: true });
			creditProviderItemStore.getProviderTypeList();
		}
		if (!isNew && providerId) {
			creditProviderItemStore.updateCreditProvider({ ...creditProviderItemStore.creditProvider, id: providerId });
			creditProviderItemStore.getProviderTypeList();
		}

		// Cleanup the store when the component unmounts
		return () => {
			creditProviderItemStore.reset();
		}

	}, [providerId, creditProviderItemStore, isNew]);

	if (creditProviderItemStore.isLoading) {
		return <InlineSpinner />;
	}

	if (creditProviderItemStore.hasError) {
		return (
			<p className="alert alert__error">
				There was an error loading the credit provider. Please try again.
			</p>
		)
	}

	if (!creditProviderItemStore.creditProvider) {
		return (
			<p className="alert alert__error">
				Credit provider not found or does not exist. Please try again later.
			</p>
		)
	}

	return (
		<div className="credit-provider">
			<Switch>
				<Route path={"/hub/settings/credit-providers/:providerId/products/:productId"} exact>
					<CreditProviderProductDetail />
				</Route>
				<Route path={"/hub/settings/credit-providers/:providerId"}>
					<>
						<PageBreadcrumbs
							tags={
								[
									{
										label: !!creditProviderItemStore.creditProvider.providerTypeName ? creditProviderItemStore.creditProvider.providerTypeName : "Reporting",
										link: "/hub/settings/credit-providers",
									},
									{
										label: creditProviderItemStore.creditProvider.name,
										link: `/hub/settings/credit-providers/${creditProviderItemStore.creditProvider.id}`,
									},
								]
							}
						/>
						{renderSection(
							"details",
							"Details",
							<>
								<div className="section-form">
									<CreditProviderForm validation={validation}/>
									<ProviderTypeSelector />
								</div>
								<div className="actions">
									<ButtonAsyncState
										display={Display.Solid}
										colors={Colors.Primary}
										sizes={Sizes.Medium}
										onPress={action(async() => {
											await creditProviderItemStore.saveCreditProviderChanges(isNew);
											if (isNew) {
												store.routerHistory.replace(`/hub/settings/credit-providers/${creditProviderItemStore.creditProvider.id}`);
											}
										})}
										readonly={!validation.isValid || creditProviderItemStore.isLoading || creditProviderItemStore.hasError}
									>
										{isNew ? "Add" : "Save" }
									</ButtonAsyncState>
								</div>
							</>
						)}
						{!isNew && !!providerId && (
							renderSection(
								"products",
								"Available Products",
								<>
									<div className="actions">
										<Button
											display={Display.Solid}
											colors={Colors.Primary}
											sizes={Sizes.Medium}
											onClick={() => setShowUploadProductModal(true)}
										>
											Add Products
										</Button>
									</div>
									<div>
										<CreditProviderProductList />
									</div>
									{showUploadProductModal && (
										<ProviderProductUploadModal
											providerId={providerId}
											providerName={creditProviderItemStore.creditProvider.name}
											onClose={() => setShowUploadProductModal(false)}
										/>
									)}
								</>
							)
						)}
						{!isNew && !!providerId && renderSection(
							"organisations",
							"Linked Organisations",
							<>
								{creditProviderItemStore.creditProvider.isActive && (
									<OrganisationProviderList providerId={providerId} />
								)}
								{!creditProviderItemStore.creditProvider.isActive && (
									<p className="alert alert__error">
										This provider is inactive and cannot be linked to any organisations.
									</p>
								)}
							</>
						)}
					</>
				</Route>
			</Switch>
		</div>
	)
})